import {QueryBuilderQuery, QueryBuilderTemplate} from "../../../index";
import React, {FunctionComponent, useCallback, useEffect, useState} from "react";
import useIsMounted from "../../hooks/use-is-mounted";
import axios, {CancelToken} from "axios";
import {CircularProgress, IconButton, MenuItem, Skeleton, TextField, TextFieldProps, Typography} from "@mui/material";
import {DeleteOutlined} from "@mui/icons-material";
import {QueryBuilderProps} from "../../index";
import {useQueryBuilderContext} from "../provider";
import QueryBuilderApiService, {RemoveTemplateRequestDS} from "../../../core/services/api";


export interface QueryBuilderSavedTemplatesSelectProps {
    name: QueryBuilderProps['name'];
    onSelect: (query: QueryBuilderQuery) => void;
}


const QueryBuilderSavedTemplatesSelect: FunctionComponent<QueryBuilderSavedTemplatesSelectProps> = (props) => {
    const {onSelect} = props;

    const {applicationName} = useQueryBuilderContext();
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState<Array<QueryBuilderTemplate>>([]);
    const [removingIds, setRemovingIds] = useState<Array<QueryBuilderTemplate['id']>>([]);
    const [open, setOpen] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Fetches the query builder templates from the server.
     */
    const fetchTemplates = useCallback(async (cancelToken: CancelToken) => {
        if (!props.name?.length)
            return;
        setLoading(true);
        const response = await QueryBuilderApiService.getTemplates(applicationName, props.name, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag && !response?.aborted) {
            setTemplates(response.data ?? []);
        }
    }, [isMounted, props.name, applicationName]);

    /**
     * As soon as the component is mounted and with each change in the [name] prop:
     * - fetch the templates for the query builder.
     */
    useEffect(() => {
        if (!open) {
            return;
        }
        const axiosCancelTokenSource = axios.CancelToken.source();
        fetchTemplates(axiosCancelTokenSource.token).then();
        return () => axiosCancelTokenSource.cancel();
    }, [fetchTemplates, open]);

    /**
     * Invokes the [onSelect] callback with the selected template.
     */
    const onTemplateSelected = useCallback<Required<TextFieldProps>['onChange']>((e) => {
        const template = templates.find(a => a.id.toString() === e.target.value.toString()) ?? null
        if (!template)
            return;
        onSelect(template.data);
    }, [onSelect, templates]);

    /**
     * Removes the template from the server.
     */
    const removeTemplate = useCallback(async (e: React.MouseEvent, template: QueryBuilderTemplate) => {
        e.stopPropagation();
        e.preventDefault();
        const forApi: RemoveTemplateRequestDS = {
            applicationName: applicationName,
            name: props.name ?? "",
            templates: templates,
            templateId: template.id,
        }
        setRemovingIds(prevState => [...new Set([...prevState, template.id])]);
        const response = await QueryBuilderApiService.removeTemplate(forApi);
        if (!isMounted())
            return;
        setRemovingIds(prevState => prevState.filter(a => a !== template.id));
        if (response?.resultFlag) {
            setTemplates(prevState => prevState.filter(a => a.id !== template.id));
        }
    }, [isMounted, props.name, templates, applicationName]);

    return (
        <>
            <TextField
                sx={(theme) => ({
                    '& .MuiFilledInput-root': {
                        backgroundColor: 'background.paper',
                        borderRadius: (theme as any).vars.shape.borderRadius,
                    },
                    '& .MuiFilledInput-underline:before': {
                        display: 'none',
                    },
                    '& .MuiFilledInput-underline:after': {
                        display: 'none',
                    },
                    '& .MuiSelect-select': {
                        paddingBlock: 1.5,
                    }
                })}
                select
                size={'small'}
                disabled={loading}
                value={""}
                variant={'filled'}
                onChange={onTemplateSelected}
                SelectProps={{
                    renderValue: () => loading ? "Loading templates..." : "Select from saved templates",
                    displayEmpty: true,
                    onOpen: () => setOpen(true),
                    onClose: () => setOpen(false),
                }}
                InputProps={{
                    disableUnderline: true,
                }}
            >
                {
                    loading &&
                    Array.from({length: 4}).map((_, a) => (
                        <MenuItem key={a} disabled value={undefined} sx={{paddingBlock: 0}}>
                            <Skeleton
                                variant="rectangular"
                                width={'100%'}
                                sx={(theme) => ({minHeight: 40, width: '100%', borderRadius: (theme as any).vars?.shape.borderRadius})}
                            />
                        </MenuItem>
                    ))
                }
                {
                    !loading && !templates.length &&
                    <MenuItem disabled value={undefined}>
                        <Typography variant={'body2'}>
                            No templates found.
                        </Typography>
                    </MenuItem>
                }
                {
                    !loading &&
                    templates.map(template => {
                        const disabled = removingIds.includes(template.id);
                        return (
                            <MenuItem
                                key={template.id}
                                value={template.id}
                                disabled={disabled}
                                sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}
                            >
                                <Typography variant={'body2'}>
                                    {template.title}
                                </Typography>
                                <IconButton
                                    color={'error'}
                                    size={'small'}
                                    onClick={(e) => removeTemplate(e, template)}
                                    disabled={disabled}
                                >
                                    {
                                        disabled
                                            ? <CircularProgress size={20} color={'error'}/>
                                            : <DeleteOutlined/>
                                    }
                                </IconButton>
                            </MenuItem>
                        );
                    })
                }
            </TextField>
        </>
    );
}

export default QueryBuilderSavedTemplatesSelect;
